import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, authService } from '../services/auth';
import { tokenManager } from '../services/api';

interface AuthState {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;

    // Actions
    setUser: (user: User) => void;
    login: (user: User, accessToken: string, refreshToken: string) => void;
    logout: () => void;
    setLoading: (loading: boolean) => void;
    checkAuth: () => boolean;
    verifyOTP: (phoneNumber: string, code: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            isAuthenticated: false,
            isLoading: false,

            setUser: (user) => set({ user }),

            login: (user, accessToken, refreshToken) => {
                tokenManager.setTokens(accessToken, refreshToken);
                set({
                    user,
                    isAuthenticated: true,
                    isLoading: false
                });
            },

            verifyOTP: async (phoneNumber: string, code: string) => {
                set({ isLoading: true });
                try {
                    const response = await authService.verifyOTP(phoneNumber, code);
                    tokenManager.setTokens(response.access_token, response.refresh_token);
                    set({
                        user: response.user,
                        isAuthenticated: true,
                        isLoading: false
                    });
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            logout: () => {
                tokenManager.clearTokens();
                set({
                    user: null,
                    isAuthenticated: false
                });
            },

            setLoading: (isLoading) => set({ isLoading }),

            checkAuth: () => {
                const hasTokens = tokenManager.hasTokens();
                const state = get();

                if (!hasTokens && state.isAuthenticated) {
                    set({ user: null, isAuthenticated: false });
                    return false;
                }

                return hasTokens && state.isAuthenticated;
            },
        }),
        {
            name: 'raaz-auth-storage',
            partialize: (state) => ({
                user: state.user,
                isAuthenticated: state.isAuthenticated
            }),
        }
    )
);

export default useAuthStore;